import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { formatUsd } from "@/lib/marketplace/queries";
import { createBuyerRequest, listBuyerRequests } from "@/lib/marketplace/community.functions";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/requests")({
  head: () => ({
    meta: [
      { title: "Buyer requests | BeHot.Now Marketplace" },
      { name: "description", content: "Post what you need and let verified BeHot.Now sellers send you proposals." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: RequestsPage,
});

function RequestsPage() {
  const { t } = useT();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", min: "50", max: "250", days: "7" });

  const { data, isLoading } = useQuery({
    queryKey: ["mp-requests", mine, user?.id ?? null],
    enabled: Boolean(user),
    queryFn: () => listBuyerRequests({ data: { mine } }),
  });

  const create = useMutation({
    mutationFn: () =>
      createBuyerRequest({
        data: {
          title: form.title,
          description: form.description,
          budgetMinCents: Math.round(Number(form.min) * 100),
          budgetMaxCents: Math.round(Number(form.max) * 100),
          deliveryDays: Number(form.days) || 7,
        },
      }),
    onSuccess: () => {
      toast.success(t("mp.requests.toast_posted"));
      setOpen(false);
      setForm({ title: "", description: "", min: "50", max: "250", days: "7" });
      void qc.invalidateQueries({ queryKey: ["mp-requests"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mp.requests.toast_post_error")),
  });

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">{t("mp.requests.title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("mp.requests.subtitle")}
            </p>
          </div>
          <Button variant="hot" onClick={() => setOpen((v) => !v)}>
            {open ? t("mp.requests.close") : t("mp.requests.post_request")}
          </Button>
        </div>

        {open && (
          <div className="card-surface mt-6 space-y-3 p-5">
            <Input
              placeholder={t("mp.requests.title_placeholder")}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <Textarea
              rows={5}
              placeholder={t("mp.requests.description_placeholder")}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <div className="grid gap-3 sm:grid-cols-3">
              <Input
                type="number"
                min={5}
                placeholder={t("mp.requests.budget_min_placeholder")}
                value={form.min}
                onChange={(e) => setForm({ ...form, min: e.target.value })}
              />
              <Input
                type="number"
                min={5}
                placeholder={t("mp.requests.budget_max_placeholder")}
                value={form.max}
                onChange={(e) => setForm({ ...form, max: e.target.value })}
              />
              <Input
                type="number"
                min={1}
                placeholder={t("mp.requests.days_placeholder")}
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
              />
            </div>
            <Button variant="hot" disabled={create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? t("mp.requests.posting") : t("mp.requests.post_request")}
            </Button>
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <Button variant={mine ? "outline" : "hot"} size="sm" onClick={() => setMine(false)}>
            {t("mp.requests.open_requests")}
          </Button>
          <Button variant={mine ? "hot" : "outline"} size="sm" onClick={() => setMine(true)}>
            {t("mp.requests.my_requests")}
          </Button>
        </div>

        {isLoading && <Skeleton className="mt-6 h-24 w-full" />}
        {data && data.length === 0 && <p className="mt-8 text-sm text-muted-foreground">{t("mp.requests.empty")}</p>}

        <ul className="mt-5 space-y-3">
          {(data ?? []).map((r) => (
            <li key={r.id}>
              <Link
                to="/marketplace/request/$requestId"
                params={{ requestId: r.id }}
                className="card-surface block p-4 transition-shadow hover:shadow-md"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold">{r.title}</p>
                  <Badge variant="secondary">
                    {formatUsd(r.budget_min_cents)} – {formatUsd(r.budget_max_cents)}
                  </Badge>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.description}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {r.buyer?.display_name || r.buyer?.handle || t("mp.common.buyer")} · {r.status}
                  {r.delivery_days ? ` · ${t("mp.order.days", { n: r.delivery_days })}` : ""}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </MarketplaceGate>
  );
}
